import React, {
    createContext,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useState,
} from 'react';
import {Icon, Loader} from 'shared/helpers';
import {ConfirmationContainer} from 'components/customer/Confirmation';
import {useAppContext} from 'contexts/ApplicationContext';
import {Help} from 'components/customer/Confirmation/Gateways/Stripe/PaymentConfirmation';
import {Container} from 'components/customer/Confirmation/JobConfirmation';
import {PaymentGateways} from 'components/customer/Confirmation/Payment/PaymentGateways';
import {usePaymentOptions} from 'components/customer/Confirmation/Payment/helpers/usePaymentOptions';
import {PaymentIntentOrSetupIntentResult} from '@stripe/stripe-js';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import styled from 'styled-components';
import {smallDevices} from 'shared/Breakpoints';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {Navigate} from 'react-router-dom';
import {Alert} from 'react-bootstrap';
import {useAppSelector} from 'store/customer';
import {selectProcessingPayment} from 'components/customer/Confirmation/store/confirmationSlice';

type ExtraActionType = {
    action: (clientSecret: string) => Promise<PaymentIntentOrSetupIntentResult>;
};
interface PaymentContextInterface {
    extraActionHandler?: ExtraActionType;
    setExtraActionHandler?: Dispatch<SetStateAction<ExtraActionType>>;
    isBalancePayment?: boolean;
}

const PaymentContext = createContext<PaymentContextInterface>({});

export const usePaymentContext = () => useContext(PaymentContext);

interface PaymentProps {
    isBalancePayment?: boolean;
}

export const Payment = ({isBalancePayment = false}: PaymentProps) => {
    const {job} = useJobContext() as PartialJob;
    const {userProfile} = useAppContext();
    const {isLoading} = usePaymentOptions();
    const [extraActionHandler, setExtraActionHandler] =
        useState<ExtraActionType>();
    const [shouldRedirect, setShouldRedirect] = useState(false);

    const processingPayment = useAppSelector(selectProcessingPayment);

    useEffect(() => {
        if (
            !isBalancePayment &&
            job &&
            job.status &&
            job.status != JOB_STATUS.PROCESSING &&
            job.status != JOB_STATUS.JOB_PENDING_CONFIRMATION
        ) {
            setShouldRedirect(true);
        }
    }, []);

    if (isLoading) {
        return <Loader loader />;
    }

    if (shouldRedirect) {
        return <Navigate to="/v2" />;
    }

    return (
        <LocalConfirmationContainer $clamp $noMargin={false}>
            <h1>
                <Icon iconName="cart-white-bg.svg" />
                <label>
                    Payment - #{job?.displayId} {job?.name}
                </label>
            </h1>

            {processingPayment ? (
                <Message variant="info">
                    <Icon iconName="product_not_found.svg" />
                    Your payment is in progress. Please do not close this
                    window.
                </Message>
            ) : null}

            {userProfile?.availablePaymentGateways ? (
                <PaymentContext.Provider
                    value={{
                        extraActionHandler,
                        setExtraActionHandler,
                        isBalancePayment,
                    }}>
                    <PaymentGateways />
                </PaymentContext.Provider>
            ) : (
                <Container>
                    <Icon iconName="product_not_found.svg" />
                    <h1>No payment gateways configured</h1>
                    <div>
                        No payment gateways are set up. Please contact us to
                        report this issue.
                    </div>
                    <Help $centerAlign>
                        <div />
                        <div>
                            <div>
                                <Icon iconName="email.svg" />
                                <a
                                    href={`mailto:${userProfile?.manufacturerEmail}`}>
                                    {userProfile?.manufacturerEmail}
                                </a>
                            </div>
                            <div>
                                <Icon iconName="phone.svg" />
                                <a
                                    href={`tel:${userProfile?.manufacturerPhone}`}>
                                    {userProfile?.manufacturerPhone}
                                </a>
                            </div>
                        </div>
                    </Help>
                </Container>
            )}
        </LocalConfirmationContainer>
    );
};

const LocalConfirmationContainer = styled(ConfirmationContainer)`
    ${smallDevices`
        margin-bottom: 15px;    
        width: 100%;
    `}
`;

const Message = styled(Alert)`
    > img {
        width: 30px;
        margin-right: 15px;
    }
`;
